import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LANGUAGE_NAMES, useI18n } from '@/i18n'

interface LanguageSwitcherProps {
    tone?: 'dark' | 'light'
    align?: 'start' | 'end'
    className?: string
}

export const LanguageSwitcher: FC<LanguageSwitcherProps> = ({ tone = 'dark', align = 'end', className }): ReactNode => {
    const router = useRouter()
    const { locale, locales, t } = useI18n()
    const [open, setOpen] = useState(false)
    const container = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return

        const onPointer = (event: MouseEvent) => {
            if (container.current && !container.current.contains(event.target as Node)) setOpen(false)
        }

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('mousedown', onPointer)
        document.addEventListener('keydown', onKey)

        return () => {
            document.removeEventListener('mousedown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    return (
        <div ref={container} className={cn('relative', className)}>
            <button
                type='button'
                onClick={() => setOpen(value => !value)}
                aria-expanded={open}
                aria-haspopup='true'
                aria-label={t.nav.language}
                className={cn(
                    'flex h-10 items-center gap-1.5 rounded-full px-3 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-gold',
                    tone === 'light' ? 'text-white hover:bg-white/15' : 'text-navy hover:bg-sand'
                )}
            >
                <Globe className='size-4' aria-hidden='true' />
                <span>{locale.toUpperCase()}</span>
                <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden='true' />
            </button>

            {open && (
                <ul
                    className={cn(
                        'absolute top-full z-50 mt-2 min-w-44 overflow-hidden rounded-xl border border-border bg-card p-1.5 shadow-xl animate-in fade-in slide-in-from-top-1',
                        align === 'end' ? 'end-0' : 'start-0'
                    )}
                >
                    {locales.map(code => (
                        <li key={code}>
                            <Link
                                href={router.asPath}
                                locale={code}
                                lang={code}
                                aria-current={code === locale ? 'true' : undefined}
                                className={cn(
                                    'flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-sand focus-visible:bg-sand focus-visible:outline-none',
                                    code === locale && 'font-semibold'
                                )}
                            >
                                <span>{LANGUAGE_NAMES[code]}</span>
                                {code === locale && <Check className='size-4 text-gold' aria-hidden='true' />}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
